import { connectionStatusOf } from './clientPresentation';
import type { ConnectionStatus } from './clientPresentation';
import type { ClientDrawerAction } from './clientActions';
import type { QueueClient } from '../../../types/client';

/**
 * What an adviser may do with a client, decided by the connection status alone
 * (未開戶 Inactive / 等待中 Pending / 已連接 Connected / 已中斷 Disconnected).
 *
 * This is the ONE place the per-status rules live. Every gate in ClientV2 — the
 * action dispatcher, the drawer tiles, the action row, the drawer's inline
 * editors, the row shortcuts — reads it, so the four statuses cannot drift apart
 * between the thing that renders a control and the thing that runs it.
 *
 * The vocabulary is `ClientDrawerAction` plus the two things the drawer opens
 * itself rather than routing through the action dispatcher:
 *
 *  - `edit-profile` — the drawer's own small editors (生日問候 / DISC / 家庭 /
 *    標籤). They write to the same adviser-owned factsheet and must obey the
 *    same rule.
 *  - `recruit-idea` — 招募 IDEA, a readout the drawer mounts while it is open,
 *    the way it already mounts 補資料 and the DISC playbook. It navigates
 *    nowhere, so there is nothing for the dispatcher to dispatch.
 */
export type ClientCapability = ClientDrawerAction | 'edit-profile' | 'recruit-idea';

/**
 * Everything a live adviser–client relationship allows. `delete` is not in here
 * because it is not a normal feature — see DELETABLE below.
 */
const FULL_CAPABILITIES: ClientCapability[] = [
  'portfolio',
  'add-policy',
  'export',
  // The row shortcut's door to the same page, one step further in (the sheet's
  // editor rather than the export menu). Same client policies behind it, so it
  // follows 'export' through every status — including out of 已中斷 below.
  'sos-edit',
  'analysis',
  'factsheet',
  'smart-idea',
  // The DISC readout's own door into that dialog — same dialog, same status
  // rules (SmartIdeaDialog has an explicit disconnected mode of its own).
  'smart-idea-disc',
  'meeting-note',
  // Same dialog, same adviser-owned factsheet — so they follow 'meeting-note'
  // through every status, including 已中斷.
  'meeting-record',
  'meeting-date',
  'meeting-reminder',
  'tracking',
  'tracking-summary',
  'contact',
  'reminder',
  'referral',
  'product-recommendation',
  'ai',
  // 招募 view's door onto the same 智能助理 page. Listed beside 'ai' and
  // restricted with it below, because it IS 'ai' — the only difference is that
  // the page is told which conversation it was opened for.
  'ai-recruit',
  // 招募 view's tools. Deliberately absent from RESTRICTED_BY_DISCONNECTION
  // below: each one opens adviser- or team-owned MATERIAL (a chart library, a
  // calculator, a content page, the team's own library, bot and coaching
  // surface) and reads none of the client's policy data, which is the only
  // thing a disconnection withdraws. An adviser
  // can still be recruiting someone who has revoked their portfolio access —
  // those are separate relationships, and this is the one the drawer keeps.
  'recruit-materials',
  'mdrt-calculator',
  'income-simulator',
  'recruit-hot-topic',
  'team-hub',
  'team-bot',
  'ai-coach',
  'iiqe-study',
  'recruit-quiz',
  // 保障小測驗: a game the client plays and the answers they give. It reads no
  // policy data, so a disconnection does not withdraw it either.
  'cover-quiz',
  // 招募 IDEA stays out of RESTRICTED_BY_DISCONNECTION with them, for the same
  // reason and one more: it reads the candidate's own answers and two lookup
  // tables, and touches no policy data at all. A disconnected client is still
  // someone this adviser may be recruiting.
  'recruit-idea',
  'edit-profile'
];

/**
 * Removing a client from your own list is allowed in EVERY status. Deliberately
 * not part of FULL_CAPABILITIES so that "what can I still do with this client"
 * and "may I remove this record" stay separable.
 */
const DELETABLE: ClientCapability[] = ['delete'];

/**
 * 已中斷 — the client has revoked this adviser's access from their own account.
 *
 * What is lost is exactly what touches the CLIENT's policies. The line is drawn
 * from what the SERVER does, not from what V1's markup looks like:
 *
 *  - 保單組合 — `portfolioController.getPortfolio` matches the client only with
 *    `$or: [{ passwordChangeKey }, { advisorDetails.$.connection: true }]` or an
 *    InactiveUsers row. A disconnected client matches neither, so the request
 *    fails outright.
 *  - 需求分析 — `analysisController.toCheckAuth` demands
 *    `connectionVerified && connection: true` (or inactive) and refuses.
 *  - 保單PDF / 保費提醒 — both read the same policy list
 *    (`reminderController.reminderPolicyList` queries `Portfolio` by the
 *    client's own userId), which is precisely the data the client withdrew.
 *  - 加單 — the one that WRITES. `addPortfolio` stores the policy against the
 *    client's own `userId`, and `getPortfolio` then refuses to hand it back to
 *    this adviser: the entry would vanish the moment it was saved. V1 presents
 *    it as unavailable too (`ClientExpansionPortfolio` renders its label
 *    `disabled={isDisconnected}`); only V1's click handler was left wired,
 *    which is a bug, not a permission.
 *  - 客戶轉介 — needs the client's live account to refer from.
 *  - 進度跟進 — V1 presents it as unavailable: `TrackingIcon` is forced to
 *    `INACTIVE` and the label drops 跟進中 back to 進度跟進
 *    (`ClientEntry.js:1211-1231`). It costs nothing either: the moment the
 *    client disconnects, `shareController.deleteShareList` **deletes** the
 *    adviser's active Tracking document outright, so there is normally no
 *    follow-up left to edit. `tracking-summary` is ClientV2's own control on
 *    the same record, so it follows it.
 *
 * Everything else stays, because the adviser still owns it. The factsheet
 * document is the adviser's: the server attaches it OUTSIDE the `dataAccess`
 * gate, and V1 reads it with no connection test at all (`ClientEntry.js:603`).
 * So 詳細資料, 會面筆記, 聯絡 (V1's ContactButton stays clickable in every
 * status), 方案配對 (its payload is built from `client.factsheetId` alone) and
 * the drawer's 生日/DISC/家庭/標籤 editors keep working. 智能摘要 keeps working
 * on purpose — `SmartIdeaDialog` has an explicit disconnected mode: it skips
 * the coverage fetch and shows a 已中斷 notice in that one card.
 *
 * This also has to survive: a disconnection is **reversible**. The client can
 * reconnect from their own side, and `shareController` re-uses the existing
 * `advisorDetails[].factsheetId` rather than creating a new one, so the notes,
 * tags and families written while disconnected are still there when the
 * relationship resumes. Locking the adviser out of their own records in the
 * meantime would lose work for no gain.
 */
const RESTRICTED_BY_DISCONNECTION: ClientCapability[] = [
  'portfolio',
  'add-policy',
  'export',
  // The row's door to that same sheet. Withheld with it, or the shortcut would
  // be the one way round a restriction the card respects.
  'sos-edit',
  'analysis',
  'reminder',
  'referral',
  'tracking',
  'tracking-summary',
  // 智能助理 — withheld not because the adviser loses anything they own, but
  // because the page on the other side of this door cannot serve a
  // disconnected client: its own chooser drops them from the list and clears
  // the selection outright (PolicyReviewChooseClient), so the tile led to a
  // page that immediately said 「未選擇」. A locked control that explains itself
  // is better than a door onto an empty room.
  'ai',
  // The 招募 door onto that same page, withheld for the same reason and not the
  // usual one. The other 招募 tools stay available on a disconnected candidate
  // because they open adviser- and team-owned material; this one does not open
  // material at all, it opens the page whose chooser has just dropped this
  // person from its list.
  'ai-recruit'
];

const CAPABILITIES_BY_STATUS: Record<ConnectionStatus, ReadonlySet<ClientCapability>> = {
  // 已連接 — the client's account is linked to this adviser.
  Connected: new Set([...FULL_CAPABILITIES, ...DELETABLE]),
  // 等待中 — an invitation is out, or a password reset is mid-flight. The
  // adviser still owns the record and everything on it, so nothing is withheld;
  // what the client has not done yet is accept, not revoke.
  Pending: new Set([...FULL_CAPABILITIES, ...DELETABLE]),
  // 未開戶 — no client account exists at all. The record is entirely
  // adviser-owned (server keeps `dataAccess = true` for inactive users), so
  // every adviser-side feature works; there is simply no client on the other
  // end yet.
  Inactive: new Set([...FULL_CAPABILITIES, ...DELETABLE]),
  // 已中斷 — see above.
  Disconnected: new Set(
    [...FULL_CAPABILITIES, ...DELETABLE].filter(capability => !RESTRICTED_BY_DISCONNECTION.includes(capability))
  )
};

/**
 * i18n id explaining why a control is unavailable, per status. Only 已中斷
 * withholds anything today; the other three map to null so a future restriction
 * has an obvious place to land (and so callers never have to special-case the
 * "allowed" path).
 */
const RESTRICTION_REASON_KEYS: Record<ConnectionStatus, string | null> = {
  Connected: null,
  Pending: null,
  Inactive: null,
  Disconnected: 'clientv2-locked-disconnected'
};

/** The capability set for this client's current connection status. */
export const clientCapabilitiesOf = (client: QueueClient): ReadonlySet<ClientCapability> =>
  CAPABILITIES_BY_STATUS[connectionStatusOf(client).status];

/** May the adviser do `capability` with this client? */
export const canClientAction = (client: QueueClient, capability: ClientCapability): boolean =>
  clientCapabilitiesOf(client).has(capability);

/**
 * i18n id for the tooltip/aria text on a control this client's status has
 * disabled, or null when the capability is allowed (nothing to explain).
 */
export const clientRestrictionReasonOf = (client: QueueClient, capability: ClientCapability): string | null =>
  canClientAction(client, capability) ? null : RESTRICTION_REASON_KEYS[connectionStatusOf(client).status];

/**
 * True when the status withholds enough that the drawer should say so once, up
 * front, rather than leaving the adviser to discover it one greyed-out tile at
 * a time. Derived, not a second hard-coded list: losing the client's own
 * policies is what that notice is about.
 */
export const isClientRestricted = (client: QueueClient): boolean => !canClientAction(client, 'portfolio');
